import { useAuth } from '@/context/AuthContext';
import { useGitLabStatus } from '@/hooks/useGitLab';
import { useRecentCommits, useCommitStats } from '@/hooks/useCommits';
import { useRepositories } from '@/hooks/useRepositories';
import { useAIStatus } from '@/hooks/useAI';
import { useUsers } from '@/hooks/useUsers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Users, GitBranch, GitCommit, Clock, Bot } from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const { user } = useAuth();

  // Fetch real data
  const { data: gitlabStatus } = useGitLabStatus();
  const { data: reposData } = useRepositories({ limit: 5, active: true });
  const { data: recentCommits, isLoading: commitsLoading } = useRecentCommits(10);
  const { data: commitStats } = useCommitStats();
  const { data: aiStatus } = useAIStatus();
  const { data: usersData } = useUsers({ limit: 1 });

  // Calculate stats from real data
  const stats = [
    {
      name: 'Total Users',
      value: usersData?.pagination?.total?.toString() || '0',
      icon: Users,
      change: 'Synced from LDAP',
      href: '/users',
    },
    {
      name: 'Repositories',
      value: gitlabStatus?.repoCount?.toString() || '0',
      icon: GitBranch,
      change: `${gitlabStatus?.connected ? 'Connected' : 'Disconnected'}`,
      href: '/repos',
    },
    {
      name: 'Total Commits',
      value: commitStats?.totalCommits?.toString() || '0',
      icon: GitCommit,
      change: `${commitStats?.uniqueAuthors || 0} contributors`,
      href: '/activity',
    },
    {
      name: 'AI Status',
      value: aiStatus?.available ? 'Online' : 'Offline',
      icon: Bot,
      change: aiStatus?.preferredProvider || 'No provider',
      href: '/settings',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome back, {user?.displayName || user?.username}!
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.name} className="hover:border-primary/50 transition-colors">
            {stat.href ? (
              <Link to={stat.href}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                  <stat.icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground">{stat.change}</p>
                </CardContent>
              </Link>
            ) : (
              <>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                  <stat.icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground">{stat.change}</p>
                </CardContent>
              </>
            )}
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest commits across all repositories</CardDescription>
          </CardHeader>
          <CardContent>
            {commitsLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : !recentCommits?.length ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No commits yet. Sync with GitLab to see activity.
              </p>
            ) : (
              <div className="space-y-4">
                {recentCommits.slice(0, 5).map((commit) => (
                  <div key={commit.id} className="flex items-start gap-3">
                    <GitCommit className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{commit.title}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{commit.authorName}</span>
                        <span>in</span>
                        <span className="truncate">
                          {typeof commit.repoId === 'object'
                            ? commit.repoId.name
                            : 'Unknown repo'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Clock className="h-3 w-3" />
                        {formatRelativeTime(commit.committedAt)}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <span className="text-green-600">+{commit.additions}</span>
                      {' / '}
                      <span className="text-red-600">-{commit.deletions}</span>
                    </div>
                  </div>
                ))}
                <Link
                  to="/activity"
                  className="block text-sm text-primary hover:underline text-center pt-2"
                >
                  View all activity
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Repositories */}
        <Card>
          <CardHeader>
            <CardTitle>Active Repositories</CardTitle>
            <CardDescription>Recently active repositories</CardDescription>
          </CardHeader>
          <CardContent>
            {!reposData?.data?.length ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No repositories synced yet.
              </p>
            ) : (
              <div className="space-y-4">
                {reposData.data.map((repo) => (
                  <div key={repo.id} className="flex items-center gap-3">
                    <GitBranch className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/repos/${repo.id}`}
                        className="text-sm font-medium hover:text-primary truncate block"
                      >
                        {repo.name}
                      </Link>
                      <p className="text-xs text-muted-foreground truncate">
                        {repo.gitlabPath}
                      </p>
                    </div>
                    {repo.lastCommitAt && (
                      <span className="text-xs text-muted-foreground">
                        {formatRelativeTime(repo.lastCommitAt)}
                      </span>
                    )}
                  </div>
                ))}
                <Link
                  to="/repos"
                  className="block text-sm text-primary hover:underline text-center pt-2"
                >
                  View all repositories
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Commit Activity Chart Placeholder */}
      {commitStats?.commitsByDay && commitStats.commitsByDay.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Commit Activity</CardTitle>
            <CardDescription>Commits over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] flex items-end gap-1">
              {commitStats.commitsByDay.slice(-30).map((day) => (
                <div
                  key={day.date}
                  className="flex-1 bg-primary/20 hover:bg-primary/40 transition-colors rounded-t"
                  style={{
                    height: `${Math.max(
                      10,
                      (day.count / Math.max(...commitStats.commitsByDay.map((d) => d.count))) * 100
                    )}%`,
                  }}
                  title={`${day.date}: ${day.count} commits`}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>30 days ago</span>
              <span>Today</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
